/**
 * ZIP — listing, per-entry preview, selective extraction and repacking
 * (§8 Phase 2, §21).
 *
 * §21's first rule is that listing an archive must never extract it, and that
 * is what shapes this handler. The central directory is a table at the end of
 * the file; reading it gives every name, size and compressed size without
 * touching a single byte of compressed data. So a 40 GB archive lists as fast
 * as a 4 KB one, and a decompression bomb is *described* rather than run.
 *
 * Extraction is therefore always an explicit, per-entry act with a budget
 * attached. Nothing is decompressed because the user opened the archive; things
 * are decompressed because the user asked for that entry.
 *
 * §21's second rule is that nothing extracted is ever executed. This package
 * has no way to execute anything, but it can and does *label* entries that
 * would be executable if they escaped — including the ones whose extension is
 * disguised — so the UI can refuse to hand them to the shell.
 */
import {
  DEFAULT_ZIP_LIMITS, EXECUTION_POLICY, FORMATS_BY_EXTENSION, FORMATS_BY_ID,
  formatKeyOf, isUnsafeEntryPath, readRawEntry, readZipDirectory,
} from '@moduviewer/core';
import type { ZipDirectory, ZipEntry, ZipViolation } from '@moduviewer/core';
import { inflateSync } from 'fflate';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';

const METHOD_STORE = 0;
const METHOD_DEFLATE = 8;
const PREVIEW_ENTRIES = 60;

/** Extensions the shell would execute. Never opened, never handed out (§21). */
const EXECUTABLE_EXTENSIONS: ReadonlySet<string> = new Set([
  '.exe', '.dll', '.com', '.scr', '.bat', '.cmd', '.ps1', '.psm1', '.vbs', '.vbe',
  '.js', '.jse', '.wsf', '.wsh', '.msi', '.msp', '.hta', '.cpl', '.jar', '.lnk',
  '.reg', '.sh', '.bash', '.zsh', '.command', '.app', '.pkg', '.dmg', '.deb',
  '.rpm', '.apk', '.appx', '.msix', '.scf', '.inf', '.pif',
]);

export interface ArchiveEntry {
  readonly name: string;
  /** Path segments, for building a tree without re-splitting everywhere. */
  readonly segments: readonly string[];
  readonly isDirectory: boolean;
  readonly sizeBytes: number;
  readonly compressedBytes: number;
  /** Compression ratio, or 1 for empty and stored entries. */
  readonly ratio: number;
  readonly method: number;
  readonly encrypted: boolean;
  /** True when the path would escape an extraction root (§21). */
  readonly unsafePath: boolean;
  /** Catalog format id guessed from the name, for the icon and the preview. */
  readonly formatId: string | null;
  /**
   * True when the shell would run this file. Extraction is still allowed —
   * refusing to save a file the user chose would be overreach — but it is never
   * opened in place and never handed to the system (§21, §30).
   */
  readonly executable: boolean;
  /** True when this entry can be previewed in place: a format we can open. */
  readonly previewable: boolean;
  /** False when the compression method is one this build cannot inflate. */
  readonly extractable: boolean;
}

export interface ArchiveModel {
  readonly entries: readonly ArchiveEntry[];
  /** Entries removed by the pending edit. Applied at export (§13). */
  readonly removed: ReadonlySet<string>;
  readonly totalUncompressed: number;
  readonly maxRatio: number;
  readonly fileCount: number;
  readonly directoryCount: number;
  readonly encryptedCount: number;
  readonly executableCount: number;
  readonly violations: readonly ZipViolation[];
  readonly comment: string;
}

/* ------------------------------------------------------------------ *
 * listing
 * ------------------------------------------------------------------ */

function isExecutableName(name: string): boolean {
  const base = name.slice(name.lastIndexOf('/') + 1).toLowerCase();
  const dot = base.lastIndexOf('.');
  if (dot < 0) return false;
  if (EXECUTABLE_EXTENSIONS.has(base.slice(dot))) return true;
  // §21's disguised-executable case: `report.pdf.exe` reads as a PDF in any
  // UI that shows only the first extension.
  const parts = base.split('.');
  return parts.length > 2 && EXECUTABLE_EXTENSIONS.has(`.${parts.at(-1)!}`);
}

function describe(entry: ZipEntry, canPreview: (formatId: string) => boolean): ArchiveEntry {
  const normalized = entry.name.replace(/\\/g, '/');
  const isDirectory = normalized.endsWith('/');
  const segments = normalized.split('/').filter((s) => s !== '');
  // The name is all there is to go on here — reading the entry's first bytes
  // would mean decompressing it, which is exactly what §21 forbids for a
  // listing. So this is a guess, and everything downstream treats it as one:
  // the real detection runs on the extracted bytes when the entry is opened.
  const key = isDirectory ? null : formatKeyOf(normalized);
  const formatId = key ? FORMATS_BY_EXTENSION.get(key)?.id ?? null : null;
  const executable = !isDirectory && isExecutableName(normalized);

  return {
    name: entry.name,
    segments,
    isDirectory,
    sizeBytes: entry.uncompressedSize,
    compressedBytes: entry.compressedSize,
    ratio: entry.compressedSize > 0 ? entry.uncompressedSize / entry.compressedSize : 1,
    method: entry.method,
    encrypted: entry.encrypted,
    unsafePath: entry.unsafePath,
    formatId,
    executable,
    // An executable is never previewed in place, whatever its bytes say.
    previewable: !isDirectory && !entry.encrypted && !executable
      && formatId !== null && canPreview(formatId),
    extractable: !entry.encrypted
      && (entry.method === METHOD_STORE || entry.method === METHOD_DEFLATE),
  };
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export interface ZipHandlerOptions {
  /**
   * Which formats can be previewed inside the archive.
   *
   * Injected rather than imported so the archive handler does not depend on the
   * registry that contains it — and so the answer is the truth about this
   * build, not a hard-coded list that drifts (§19).
   */
  readonly canPreview?: (formatId: string) => boolean;
}

export function createZipHandler(options: ZipHandlerOptions = {}): FormatHandler<ArchiveModel> {
  const canPreview = options.canPreview ?? (() => false);

  /** Read the directory with the tier's limits rather than the defaults. */
  function directoryOf(source: FileSource, ctx: HandlerContext): ZipDirectory {
    return readZipDirectory(source.bytes, {
      maxEntries: ctx.limits.maxArchiveEntries,
      maxTotalUncompressed: ctx.limits.maxArchiveExpansionBytes,
      maxRatio: ctx.limits.maxCompressionRatio,
    });
  }

  function build(directory: ZipDirectory, comment: string): ArchiveModel {
    const entries = directory.entries.map((e) => describe(e, canPreview));
    return {
      entries,
      removed: new Set(),
      totalUncompressed: directory.totalUncompressed,
      maxRatio: directory.maxRatio,
      fileCount: entries.filter((e) => !e.isDirectory).length,
      directoryCount: entries.filter((e) => e.isDirectory).length,
      encryptedCount: entries.filter((e) => e.encrypted).length,
      executableCount: entries.filter((e) => e.executable).length,
      violations: directory.violations,
      comment,
    };
  }

  return {
    id: 'zip',
    formatIds: ['zip'],

    async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
      throwIfAborted(ctx.signal);
      // The whole listing is this cheap: a table at the end of the file. There
      // is no "first page" to separate out because nothing is decompressed.
      const directory = directoryOf(source, ctx);
      const entries = directory.entries.slice(0, PREVIEW_ENTRIES)
        .map((e) => describe(e, canPreview));

      return {
        kind: 'archive',
        unitCount: directory.entries.length,
        unitKey: 'viewer.entry',
        firstContent: {
          entries,
          more: directory.entries.length > PREVIEW_ENTRIES,
          totalUncompressed: directory.totalUncompressed,
        },
        warnings: [],
      };
    },

    async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<ArchiveModel>> {
      throwIfAborted(ctx.signal);
      const directory = directoryOf(source, ctx);

      // A path that escapes the extraction root is a refusal, not a warning:
      // there is no safe way to offer "extract" for such an archive, and §21
      // treats it as hostile rather than merely malformed.
      const unsafe = directory.violations.filter((v) => v.kind === 'unsafe-path');
      if (unsafe.length > 0) {
        throw new HandlerError(
          'archive.violation.unsafePath',
          `entries escape the extraction root: ${unsafe.map((v) => (v.kind === 'unsafe-path' ? v.entry : '')).join(', ')}`,
        );
      }

      const model = build(directory, readComment(source.bytes));
      const spec = FORMATS_BY_ID.get(source.formatId);
      const warnings: HandlerWarning[] = [];

      if (model.encryptedCount > 0) {
        warnings.push({
          messageKey: 'error.passwordProtected',
          severity: 'caution',
          detail: `${model.encryptedCount} entries are password-protected. Their names and sizes are listed; their contents cannot be read (§21).`,
        });
      }
      if (model.executableCount > 0) {
        warnings.push({
          messageKey: 'detect.warning.executableContent',
          severity: 'caution',
          detail: `${model.executableCount} entries are executable files. Modu Viewer never runs them and never opens them in place; extraction saves them as ordinary files (§21).`,
        });
      }
      for (const violation of model.violations) {
        if (violation.kind === 'ratio-too-high') {
          warnings.push({
            messageKey: 'archive.violation.ratioTooHigh',
            severity: 'caution',
            detail: `${violation.entry} expands ${Math.round(violation.ratio)}× (limit ${violation.limit}). Listing is safe; extracting it is refused.`,
          });
        }
        if (violation.kind === 'expansion-too-large') {
          warnings.push({
            messageKey: 'archive.violation.expansionTooLarge',
            severity: 'caution',
            detail: `total expansion would be ${violation.bytes} bytes (limit ${violation.limit}).`,
          });
        }
        if (violation.kind === 'too-many-entries') {
          warnings.push({
            messageKey: 'archive.violation.tooManyEntries',
            severity: 'degraded',
            detail: `${violation.count} entries (limit ${violation.limit}); the list is truncated.`,
          });
        }
        if (violation.kind === 'zip64-unsupported') {
          warnings.push({
            messageKey: 'error.featuresUnsupported',
            severity: 'degraded',
            detail: 'This archive uses ZIP64 extensions, which this release cannot read fully.',
          });
        }
      }
      const unreadable = model.entries.filter((e) => !e.isDirectory && !e.extractable && !e.encrypted);
      if (unreadable.length > 0) {
        warnings.push({
          messageKey: 'error.featuresUnsupported',
          severity: 'degraded',
          detail: `${unreadable.length} entries use a compression method this release cannot decompress (methods ${[...new Set(unreadable.map((e) => e.method))].join(', ')}).`,
        });
      }

      return {
        formatId: source.formatId,
        model,
        readOnly: ctx.assessment.forceReadOnly,
        editLevel: spec?.edit ?? 'basic-edit',
        lossOnSave: spec?.lossOnSave ?? [],
        warnings,
      };
    },

    /**
     * Save the archive with the removed entries left out.
     *
     * Everything kept is copied as its original compressed bytes, so the files
     * inside are bit-identical and never pass through a decompress/recompress
     * cycle that could change them (§13).
     */
    async exportBytes(
      model: ArchiveModel, source: FileSource, ctx: HandlerContext,
    ): Promise<ExportResult> {
      throwIfAborted(ctx.signal);
      const directory = directoryOf(source, ctx);
      const keep = directory.entries.filter((e) => !model.removed.has(e.name));
      if (keep.length === 0) {
        throw new HandlerError('error.unableToRead', 'an archive with no entries would be empty');
      }

      const pending: PendingEntry[] = [];
      for (const entry of keep) {
        const raw = readRawEntry(source.bytes, entry);
        if (!raw) {
          throw new HandlerError('error.unableToRead', `could not read stored data for ${entry.name}`);
        }
        pending.push({
          name: entry.name,
          data: raw,
          method: entry.method,
          crc: crcFor(source.bytes, entry),
          uncompressedSize: entry.uncompressedSize,
        });
      }

      return {
        bytes: writeZip(pending),
        suggestedName: source.filename,
        formatId: source.formatId,
        dropped: [...model.removed].map((name) => `${name}: 새 압축 파일에서 제외됨`),
      };
    },

    async validateExport(
      bytes: Uint8Array, model: ArchiveModel, ctx: HandlerContext,
    ): Promise<ValidationReport> {
      throwIfAborted(ctx.signal);
      const issues: string[] = [];
      let reopened = false;
      let contentPreserved = false;

      try {
        const directory = readZipDirectory(bytes, DEFAULT_ZIP_LIMITS);
        reopened = true;

        const expected = model.entries.filter((e) => !model.removed.has(e.name));
        const actual = new Map(directory.entries.map((e) => [e.name, e]));

        if (directory.entries.length !== expected.length) {
          issues.push(`entry count changed: ${expected.length} → ${directory.entries.length}`);
        }
        for (const entry of expected) {
          const twin = actual.get(entry.name);
          if (!twin) { issues.push(`entry missing: ${entry.name}`); continue; }
          if (twin.uncompressedSize !== entry.sizeBytes) {
            issues.push(`${entry.name} size changed: ${entry.sizeBytes} → ${twin.uncompressedSize}`);
          }
          if (twin.method !== entry.method) {
            issues.push(`${entry.name} was recompressed: method ${entry.method} → ${twin.method}`);
          }
        }
        for (const name of model.removed) {
          if (actual.has(name)) issues.push(`removed entry is still present: ${name}`);
        }

        contentPreserved = issues.length === 0;
      } catch (err) {
        issues.push(`could not reopen the exported archive: ${(err as Error).message}`);
      }

      return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
    },
  };
}

/* ------------------------------------------------------------------ *
 * selective extraction
 * ------------------------------------------------------------------ */

export interface ExtractResult {
  readonly name: string;
  readonly bytes: Uint8Array;
  readonly formatId: string | null;
}

/**
 * Decompress one entry, and only on request.
 *
 * The size is checked against the declared size before allocating, and the
 * result against it afterwards: a bomb lies in the central directory, so
 * trusting the declared size alone would defeat the budget it is meant to
 * enforce.
 */
export function extractEntry(
  bytes: Uint8Array, model: ArchiveModel, name: string, ctx: HandlerContext,
): ExtractResult {
  throwIfAborted(ctx.signal);
  const described = model.entries.find((e) => e.name === name);
  if (!described) throw new HandlerError('error.unableToRead', `no entry named ${name}`);
  if (described.isDirectory) throw new HandlerError('error.unableToRead', `${name} is a directory`);
  if (described.encrypted) {
    throw new HandlerError('error.passwordProtected', `${name} is password-protected`);
  }
  if (isUnsafeEntryPath(name)) {
    throw new HandlerError('archive.violation.unsafePath', `${name} escapes the extraction root`);
  }
  if (!described.extractable) {
    throw new HandlerError(
      'error.featuresUnsupported',
      `${name} uses compression method ${described.method}`,
    );
  }
  if (described.sizeBytes > ctx.limits.maxArchiveExpansionBytes) {
    throw new HandlerError(
      'archive.violation.expansionTooLarge',
      `${name} expands to ${described.sizeBytes} bytes (limit ${ctx.limits.maxArchiveExpansionBytes})`,
    );
  }
  if (described.ratio > ctx.limits.maxCompressionRatio) {
    throw new HandlerError(
      'archive.violation.ratioTooHigh',
      `${name} expands ${Math.round(described.ratio)}× (limit ${ctx.limits.maxCompressionRatio})`,
    );
  }

  const directory = readZipDirectory(bytes, {
    maxEntries: ctx.limits.maxArchiveEntries,
    maxTotalUncompressed: ctx.limits.maxArchiveExpansionBytes,
    maxRatio: ctx.limits.maxCompressionRatio,
  });
  const entry = directory.entries.find((e) => e.name === name);
  if (!entry) throw new HandlerError('error.unableToRead', `no entry named ${name}`);

  const raw = readRawEntry(bytes, entry);
  if (!raw) throw new HandlerError('error.damagedOrIncomplete', `${name} has no readable data`);

  let out: Uint8Array;
  if (entry.method === METHOD_STORE) {
    out = raw;
  } else {
    try {
      // Bounding the output buffer is the actual defence: fflate writes into it
      // and stops, so a lying header cannot expand past the declared size.
      out = inflateSync(raw, { out: new Uint8Array(entry.uncompressedSize) });
    } catch (err) {
      throw new HandlerError('error.damagedOrIncomplete', `could not decompress ${name}: ${(err as Error).message}`);
    }
  }

  if (out.length !== entry.uncompressedSize) {
    throw new HandlerError(
      'error.damagedOrIncomplete',
      `${name} decompressed to ${out.length} bytes, not the declared ${entry.uncompressedSize}`,
    );
  }

  return { name, bytes: out, formatId: described.formatId };
}

/** Queue an entry for removal. Nothing is written until export (§13). */
export function removeEntries(model: ArchiveModel, names: readonly string[]): ArchiveModel {
  const removed = new Set(model.removed);
  for (const name of names) {
    if (!model.entries.some((e) => e.name === name)) {
      throw new HandlerError('error.unableToRead', `no entry named ${name}`);
    }
    removed.add(name);
    // Removing a folder removes what is inside it — leaving orphans behind
    // would produce an archive whose tree does not match what was shown.
    const entry = model.entries.find((e) => e.name === name)!;
    if (entry.isDirectory) {
      const prefix = name.replace(/\\/g, '/');
      for (const child of model.entries) {
        if (child.name.replace(/\\/g, '/').startsWith(prefix) && child.name !== name) {
          removed.add(child.name);
        }
      }
    }
  }
  return { ...model, removed };
}

/** Undo a queued removal. */
export function restoreEntries(model: ArchiveModel, names: readonly string[]): ArchiveModel {
  const removed = new Set(model.removed);
  for (const name of names) removed.delete(name);
  return { ...model, removed };
}

/* ------------------------------------------------------------------ *
 * tree
 * ------------------------------------------------------------------ */

export interface ArchiveNode {
  readonly name: string;
  readonly path: string;
  readonly isDirectory: boolean;
  readonly entry: ArchiveEntry | null;
  readonly children: readonly ArchiveNode[];
  /** Total uncompressed size of everything beneath, for a folder. */
  readonly sizeBytes: number;
}

/**
 * Build a folder tree from the flat entry list.
 *
 * Directories are synthesised where an archive only names files: many writers
 * omit directory entries entirely, and a viewer that showed a flat list of
 * `a/b/c.txt` would be harder to read than the folder the user zipped.
 */
export function buildTree(model: ArchiveModel): ArchiveNode {
  interface Building {
    name: string;
    path: string;
    isDirectory: boolean;
    entry: ArchiveEntry | null;
    children: Map<string, Building>;
  }

  const root: Building = { name: '', path: '', isDirectory: true, entry: null, children: new Map() };

  for (const entry of model.entries) {
    if (model.removed.has(entry.name)) continue;
    let node = root;
    entry.segments.forEach((segment, i) => {
      const last = i === entry.segments.length - 1;
      const path = entry.segments.slice(0, i + 1).join('/');
      let child = node.children.get(segment);
      if (!child) {
        child = {
          name: segment,
          path,
          isDirectory: !last || entry.isDirectory,
          entry: null,
          children: new Map(),
        };
        node.children.set(segment, child);
      }
      if (last) {
        child.entry = entry;
        child.isDirectory = entry.isDirectory;
      }
      node = child;
    });
  }

  const finish = (node: Building): ArchiveNode => {
    const children = [...node.children.values()]
      .map(finish)
      // Folders first, then names — the order every file manager uses, and the
      // one a list of an unfamiliar archive is easiest to scan in.
      .sort((a, b) => (
        a.isDirectory === b.isDirectory
          ? a.name.localeCompare(b.name, 'ko')
          : a.isDirectory ? -1 : 1
      ));
    return {
      name: node.name,
      path: node.path,
      isDirectory: node.isDirectory,
      entry: node.entry,
      children,
      sizeBytes: node.entry && !node.isDirectory
        ? node.entry.sizeBytes
        : children.reduce((sum, c) => sum + c.sizeBytes, 0),
    };
  };

  return finish(root);
}

/**
 * Whether the policy allows handing this entry to the system.
 *
 * Reads the frozen policy rather than re-deciding: §30 states executables are
 * blocked, and duplicating that judgement here is how the two drift apart.
 */
export function mayOpenExternally(entry: ArchiveEntry): boolean {
  if (entry.executable) return EXECUTION_POLICY.embeddedExecutables !== 'blocked';
  return EXECUTION_POLICY.externalApplications !== 'blocked';
}

/* ------------------------------------------------------------------ *
 * writing
 * ------------------------------------------------------------------ */

interface PendingEntry {
  readonly name: string;
  readonly data: Uint8Array;
  readonly method: number;
  readonly crc: number;
  readonly uncompressedSize: number;
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Lift the CRC the archive already recorded, rather than decompressing to recompute it. */
function crcFor(bytes: Uint8Array, entry: ZipEntry): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const p = entry.localHeaderOffset;
  if (p + 30 <= bytes.length && view.getUint32(p, true) === 0x04034b50) {
    const flags = view.getUint16(p + 6, true);
    const stored = view.getUint32(p + 14, true);
    // With bit 3 set the local header's CRC is zero and the real one lives in a
    // trailing data descriptor; recomputing is then the only correct answer.
    if ((flags & 0x08) === 0 && stored !== 0) return stored;
  }
  const raw = readRawEntry(bytes, entry);
  if (!raw) return 0;
  if (entry.method === METHOD_STORE) return crc32(raw);
  try {
    return crc32(inflateSync(raw, { out: new Uint8Array(entry.uncompressedSize) }));
  } catch {
    return 0;
  }
}

/** The archive-level comment, which is metadata worth showing (§31). */
function readComment(bytes: Uint8Array): string {
  if (bytes.length < 22) return '';
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let i = bytes.length - 22; i >= 0; i--) {
    if (view.getUint32(i, true) !== 0x06054b50) continue;
    const length = view.getUint16(i + 20, true);
    if (length === 0) return '';
    return new TextDecoder('utf-8').decode(bytes.subarray(i + 22, i + 22 + length));
  }
  return '';
}

function writeZip(entries: readonly PendingEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const e of entries) {
    const nameBytes = encoder.encode(e.name);

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, 0x0800, true);        // UTF-8 names
    lv.setUint16(8, e.method, true);
    lv.setUint16(10, 0, true);
    lv.setUint16(12, 0x21, true);         // fixed date, so saves are reproducible
    lv.setUint32(14, e.crc, true);
    lv.setUint32(18, e.data.length, true);
    lv.setUint32(22, e.uncompressedSize, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    local.set(nameBytes, 30);
    locals.push(local, e.data);

    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, e.method, true);
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 0x21, true);
    cv.setUint32(16, e.crc, true);
    cv.setUint32(20, e.data.length, true);
    cv.setUint32(24, e.uncompressedSize, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint32(42, offset, true);
    central.set(nameBytes, 46);
    centrals.push(central);

    offset += local.length + e.data.length;
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  const out = new Uint8Array(offset + centralSize + eocd.length);
  let p = 0;
  for (const chunk of locals) { out.set(chunk, p); p += chunk.length; }
  for (const chunk of centrals) { out.set(chunk, p); p += chunk.length; }
  out.set(eocd, p);
  return out;
}
